'use client';

import type { FC, RefObject } from 'react';
import { useMemo } from 'react';
import { OverlayBadge } from '../../components/ui';
import { InfiniteItemGrid } from '../../components/InfiniteItemGrid';
import { PagedItemGrid } from '../../components/PagedItemGrid';
import { useInfinitePager } from '../../hooks/useInfinitePager';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import type { AvailableUpgradeTargetItem } from '../../services/api';
import { ItemListItem } from '../../components/ItemListItem';
import { ItemPrice, useSteamPriceAllowed } from '../../components/ItemPrice';
import { useUpgradeTargetsInfinite } from '../../queries/useUpgradeQueries';
import type { UpgradeTargetQueryFilters } from './useUpgradeTargetFilters';
import translations from './UpgradeScreen.translations';
import { cn } from '../../utils/cn';
import { UPGRADE_COMPACT_CARD_CLASS, UPGRADE_COMPACT_MIN_COLUMN, UPGRADE_SELECTED_CARD_CLASS } from './upgradeCards';

interface UpgradeTargetListProps {
  sourceItemId: string | null;
  sourceAmount?: number | null;
  queryFilters: UpgradeTargetQueryFilters;
  onSelectItem: (item: AvailableUpgradeTargetItem) => void;
  scrollRef: RefObject<HTMLDivElement | null>;
  paged?: boolean;
  selectedHashName?: string | null;
}

export const UpgradeTargetList: FC<UpgradeTargetListProps> = ({
  sourceItemId,
  sourceAmount = null,
  queryFilters,
  onSelectItem,
  scrollRef,
  paged = false,
  selectedHashName = null,
}) => {
  const { t } = useLocalTranslation(translations);
  const query = useUpgradeTargetsInfinite(sourceItemId, sourceAmount, queryFilters);
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
    refetch,
  } = query;
  const pager = useInfinitePager(query, `${sourceItemId ?? sourceAmount}:${JSON.stringify(queryFilters)}`);

  const targetItems = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  );

  const steamPriceAllowed = useSteamPriceAllowed();

  const renderItem = (item: AvailableUpgradeTargetItem, index: number) => {
    const price = item.price || 0;

    return (
      <ItemListItem
        key={`${item.hashName}-${index}`}
        item={item}
        className={cn(paged && UPGRADE_COMPACT_CARD_CLASS, item.hashName === selectedHashName && UPGRADE_SELECTED_CARD_CLASS)}
        onClick={() => onSelectItem(item)}
      >
        <div className="flex flex-col items-start gap-1">
          <OverlayBadge>
            <ItemPrice credits={price} variant="cardPrice" />
          </OverlayBadge>
          <OverlayBadge>
            <span className="flex flex-row items-center gap-2 text-xs">
              <span className="text-accent-green font-semibold">{(item.probability * 100).toFixed(1)}%</span>
              <span className="text-accent-blue font-semibold">x{item.coefficient.toFixed(2)}</span>
            </span>
          </OverlayBadge>
          {steamPriceAllowed && item.steamPrice != null && (
            <OverlayBadge>
              <ItemPrice steamPrice={item.steamPrice} steamIconSize={11} variant="steamPrice" />
            </OverlayBadge>
          )}
        </div>
      </ItemListItem>
    );
  };

  if (paged) {
    return (
      <PagedItemGrid
        items={pager.current?.items ?? []}
        page={pager.page}
        totalPages={pager.current?.meta.totalPages ?? null}
        hasPrev={pager.hasPrev}
        hasNext={pager.hasNext}
        onPrev={pager.prev}
        onNext={pager.next}
        isLoading={isLoading}
        isError={isError}
        refetch={() => refetch()}
        renderItem={renderItem}
        emptyTitle={t('noTargetsFound')}
        errorText={t('errorLoadTargets')}
        minColumn={UPGRADE_COMPACT_MIN_COLUMN}
        skeletonCardClassName={UPGRADE_COMPACT_CARD_CLASS}
      />
    );
  }

  return (
    <InfiniteItemGrid
      items={targetItems}
      isLoading={isLoading}
      isError={isError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      fetchNextPage={() => fetchNextPage()}
      refetch={() => refetch()}
      scrollRef={scrollRef}
      renderItem={renderItem}
      emptyTitle={t('noTargetsFound')}
      errorText={t('errorLoadTargets')}
    />
  );
};
